import { Check, Coins } from "@repo/ui/components/icons";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import type { ReactNode } from "react";

import type { CatalogProduct } from "../../../lib/api-client";

// The pieces the buy and sell wizards on /buy share (F118, F125): the step
// bar, back/next, a selectable tile and a bar's thumbnail.

const fa = (n: number) => n.toLocaleString("fa-IR");

export function Progress<S extends number>({ steps, step, reachable, onPick, label }: { steps: readonly string[]; step: S; reachable: S; onPick: (s: S) => void; label: string }) {
  return (
    <ol className="bg-background flex items-center gap-1 rounded-full p-1 shadow-sm" aria-label={label}>
      {steps.map((name, i) => {
        const s = (i + 1) as S;
        const done = s < step;
        // The current step's name shows in full; the others shrink to their number on phones.
        return (
          <li key={name} className={cn("min-w-0", s === step ? "flex-[3] sm:flex-1" : "flex-1")}>
            <button
              type="button"
              disabled={s > reachable}
              onClick={() => onPick(s)}
              aria-current={s === step ? "step" : undefined}
              className={cn(
                "flex w-full items-center justify-center gap-1.5 rounded-full px-2 py-1.5 text-xs transition-colors disabled:cursor-default sm:text-sm",
                s === step ? "bg-primary text-primary-foreground font-semibold" : done ? "text-foreground hover:bg-muted" : "text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full text-caption",
                  s === step ? "bg-primary-foreground text-primary" : done ? "bg-tint-green text-ink-green" : "bg-muted",
                )}
              >
                {done ? <Check size={11} /> : fa(s)}
              </span>
              <span className={cn("whitespace-nowrap", s !== step && "hidden sm:inline")}>{name}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export function StepButtons({ step, last, canNext, onBack, onNext }: { step: number; last: number; canNext: boolean; onBack: () => void; onNext: () => void }) {
  return (
    <>
      <Button type="button" variant="secondary" onClick={onBack} disabled={step === 1} className="disabled:invisible">
        قبلی
      </Button>
      {step < last ? (
        <Button type="button" onClick={onNext} disabled={!canNext} className="px-8">
          بعدی
        </Button>
      ) : null}
    </>
  );
}

/** A selectable tile: a ring and a check when chosen. */
export function Choice({
  selected,
  disabled,
  onClick,
  className,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        "bg-surface relative flex flex-col gap-1 rounded-2xl p-4 text-start ring-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        selected ? "bg-brand-tint/60 ring-brand-strong" : "ring-transparent hover:bg-muted",
        className,
      )}
    >
      {selected ? (
        <span className="bg-brand-strong absolute end-2.5 top-2.5 flex size-5 items-center justify-center rounded-full text-primary-foreground">
          <Check size={12} />
        </span>
      ) : null}
      {children}
    </button>
  );
}

export function BarThumb({ product, size = "size-20" }: { product: CatalogProduct; size?: string }) {
  const image = product.images[0];
  return (
    <span className={cn("bg-background relative shrink-0 overflow-hidden rounded-xl", size)}>
      {image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={image.url} alt={image.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <Coins size={24} className="text-muted-foreground absolute inset-0 m-auto opacity-50" />
      )}
    </span>
  );
}


export function unique<T>(items: T[], key: (item: T) => string) {
  const seen = new Set<string>();
  return items.filter((item) => (seen.has(key(item)) ? false : (seen.add(key(item)), true)));
}
